import React, { useState, useEffect } from 'react';
import { apiCall } from './config';

function Login({ onLogin }) {
  const [lastname, setLastname] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  // Prüfe auf automatischen Login beim Laden der Komponente
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const autoLoginData = urlParams.get('autoLogin');
    
    if (autoLoginData) {
      try {
        const loginData = JSON.parse(decodeURIComponent(autoLoginData));
        
        // Setze die Login-Daten
        setLastname(loginData.username || '');
        
        // Formatiere das Geburtsdatum für die Anzeige
        if (loginData.password && loginData.password.length === 8) {
          const day = loginData.password.substring(0, 2);
          const month = loginData.password.substring(2, 4);
          const year = loginData.password.substring(4, 8);
          setBirthdate(`${day}.${month}.${year}`);
        }
        
        // Führe automatischen Login aus
        performAutoLogin(loginData.username, loginData.password);
        
        // Entferne den autoLogin Parameter aus der URL
        window.history.replaceState({}, document.title, window.location.pathname);
        
      } catch (error) {
        console.error('Fehler beim automatischen Login:', error);
        setError('Ungültiger Login-Link.');
      }
    }
  }, []);

  // Funktion für automatischen Login
  const performAutoLogin = async (username, password) => {
    setLoading(true);
    setError('');
    
    try {
      // Verwende die gleiche Geburtsdatum-Verarbeitung wie beim manuellen Login
      let birthdateForBackend = '';
      
      if (password && password.length === 8) {
        // Bereits im TTMMYYYY Format
        birthdateForBackend = password;
      } else if (password && password.includes('.')) {
        // TT.MM.JJJJ -> TTMMYYYY konvertieren
        const parts = password.split('.');
        if (parts.length === 3) {
          const day = parts[0].padStart(2, '0');
          const month = parts[1].padStart(2, '0');
          const year = parts[2];
          birthdateForBackend = `${day}${month}${year}`;
        }
      } else {
        birthdateForBackend = password;
      }
      
      console.log('Auto-Login Daten:', { 
        lastname: username, 
        birthdate: birthdateForBackend 
      });

      const response = await apiCall('/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lastname: username,
          birthdate: birthdateForBackend
        })
      });

      // Backend sendet bei Erfolg ein Objekt mit 'user' und 'token', kein 'success' Flag
      if (response.user && response.token) {
        localStorage.setItem('token', response.token);
        onLogin(response.user);
      } else {
        setError(response.message || 'Login fehlgeschlagen');
      }
    } catch (error) {
      console.error('Auto-Login Fehler:', error);
      setError('Automatischer Login fehlgeschlagen. Bitte manuell anmelden.');
    } finally {
      setLoading(false);
    }
  };

  // Formatiere Geburtsdatum automatisch während der Eingabe
  const handleBirthdateChange = (e) => {
    let value = e.target.value.replace(/\D/g, ''); // Nur Ziffern
    
    if (value.length >= 2) {
      value = value.slice(0, 2) + '.' + value.slice(2);
    }
    if (value.length >= 5) {
      value = value.slice(0, 5) + '.' + value.slice(5, 9);
    }
    
    setBirthdate(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!lastname.trim()) {
      setError('Bitte geben Sie Ihren Nachnamen ein');
      return;
    }
    
    // Konvertiere Geburtsdatum zu TTMMYYYY Format für Backend
    let birthdateForBackend = '';
    
    if (birthdate && birthdate.includes('.')) {
      // TT.MM.JJJJ -> TTMMYYYY konvertieren
      const parts = birthdate.split('.');
      if (parts.length === 3) {
        const day = parts[0].padStart(2, '0');
        const month = parts[1].padStart(2, '0');
        const year = parts[2];
        birthdateForBackend = `${day}${month}${year}`;
      }
    } else if (birthdate && birthdate.length === 8) {
      // Bereits im TTMMYYYY Format
      birthdateForBackend = birthdate;
    } else if (birthdate) {
      // Fallback: direkt verwenden
      birthdateForBackend = birthdate;
    }
    
    // Debug: Zeige was gesendet wird
    console.log('Sending login data:', { 
      lastname: lastname.trim(), 
      birthdate: birthdateForBackend, 
      password: password || undefined 
    });
    
    setLoading(true);
    try {
      const requestData = { 
        lastname: lastname.trim(), 
        birthdate: birthdateForBackend 
      };
      
      // Nur Passwort hinzufügen wenn es nicht leer ist
      if (password && password.trim()) {
        requestData.password = password.trim();
      }
      
      const data = await apiCall('/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      
      console.log('Login successful:', data);
      if (data.token) {
        localStorage.setItem('token', data.token);
      }
      onLogin(data.user || data);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex flex-column"
         style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
      
      {/* Logo / Header */}
      <div className="text-center pt-4 pt-md-5 pb-3">
        <i className="bi bi-cup-hot-fill text-warning" style={{fontSize: 'clamp(3rem, 8vw, 5rem)', filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))'}}></i>
        <h1 className="text-white fw-bold mt-2" style={{fontSize: 'clamp(1.8rem, 5vw, 3rem)', textShadow: '0 2px 8px rgba(0,0,0,0.3)'}}>
          Oktoberfest
        </h1>
        <p className="text-white-50 mb-0" style={{fontSize: 'clamp(0.9rem, 2.5vw, 1.3rem)'}}>
          Personalplanung
        </p>
      </div>

      {/* Kacheln Container */}
      <div className="flex-grow-1 d-flex align-items-start align-items-md-center justify-content-center px-3 pb-4">
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
          gap: 'clamp(12px, 2vw, 24px)',
          width: '100%',
          maxWidth: '900px'
        }}>
          
          {/* Kachel 1: Login */}
          <div
            onClick={() => setShowLogin(true)}
            className="card border-0 shadow-lg"
            style={{
              background: 'linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s',
              aspectRatio: window.innerWidth >= 768 ? '1' : 'auto',
              minHeight: window.innerWidth < 768 ? '140px' : undefined,
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.3)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
          >
            <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-3">
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: '50%',
                width: 'clamp(60px, 10vw, 80px)',
                height: 'clamp(60px, 10vw, 80px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                border: '2px solid rgba(255,255,255,0.3)',
                marginBottom: '0.75rem'
              }}>
                🍺
              </div>
              <h5 className="text-white fw-bold mb-1" style={{fontSize: 'clamp(1.1rem, 3vw, 1.4rem)'}}>
                Mitarbeiter Login
              </h5>
              <p className="text-white-50 mb-0" style={{fontSize: 'clamp(0.75rem, 2vw, 0.95rem)'}}>
                Anmeldung für Mitarbeiter & Admins
              </p>
            </div>
          </div>

          {/* Kachel 2: Bewerben */}
          <a href="/bewerben" className="text-decoration-none">
            <div
              className="card border-0 shadow-lg h-100"
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                aspectRatio: window.innerWidth >= 768 ? '1' : 'auto',
                minHeight: window.innerWidth < 768 ? '140px' : undefined,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-3">
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  width: 'clamp(60px, 10vw, 80px)',
                  height: 'clamp(60px, 10vw, 80px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  marginBottom: '0.75rem'
                }}>
                  📋
                </div>
                <h5 className="text-white fw-bold mb-1" style={{fontSize: 'clamp(1.1rem, 3vw, 1.4rem)'}}>
                  Jetzt bewerben
                </h5>
                <p className="text-white-50 mb-0" style={{fontSize: 'clamp(0.75rem, 2vw, 0.95rem)'}}>
                  Als Mitarbeiter/in registrieren
                </p>
                <p className="text-white mb-0 mt-2" style={{fontSize: 'clamp(0.7rem, 1.8vw, 0.85rem)', opacity: 0.9}}>
                  💬 oder direkt per WhatsApp: +49 176 43831566
                </p>
              </div>
            </div>
          </a>

          {/* Kachel 3: Rollstuhlservice */}
          <a href="/rollstuhl" className="text-decoration-none">
            <div
              className="card border-0 shadow-lg h-100"
              style={{
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                aspectRatio: window.innerWidth >= 768 ? '1' : 'auto',
                minHeight: window.innerWidth < 768 ? '140px' : undefined,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-3">
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  width: 'clamp(60px, 10vw, 80px)',
                  height: 'clamp(60px, 10vw, 80px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  marginBottom: '0.75rem'
                }}>
                  🦽
                </div>
                <h5 className="text-white fw-bold mb-1" style={{fontSize: 'clamp(1.1rem, 3vw, 1.4rem)'}}>
                  Rollstuhlservice
                </h5>
                <p className="text-white-50 mb-0" style={{fontSize: 'clamp(0.75rem, 2vw, 0.95rem)'}}>
                  Kostenlos für Besucher
                </p>
              </div>
            </div>
          </a>

          {/* Kachel 4: Gepäck */}
          <a href="/gepaeck-suchen" className="text-decoration-none">
            <div
              className="card border-0 shadow-lg h-100"
              style={{
                background: 'linear-gradient(135deg, #fd7e14 0%, #e65100 100%)',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                aspectRatio: window.innerWidth >= 768 ? '1' : 'auto',
                minHeight: window.innerWidth < 768 ? '140px' : undefined,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.3)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = ''; }}
            >
              <div className="card-body d-flex flex-column align-items-center justify-content-center text-center p-3">
                <div style={{
                  background: 'rgba(255,255,255,0.2)',
                  borderRadius: '50%',
                  width: 'clamp(60px, 10vw, 80px)',
                  height: 'clamp(60px, 10vw, 80px)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 'clamp(1.8rem, 5vw, 2.5rem)',
                  border: '2px solid rgba(255,255,255,0.3)',
                  marginBottom: '0.75rem'
                }}>
                  🧳
                </div>
                <h5 className="text-white fw-bold mb-1" style={{fontSize: 'clamp(1.1rem, 3vw, 1.4rem)'}}>
                  Gepäck vermisst?
                </h5>
                <p className="text-white-50 mb-0" style={{fontSize: 'clamp(0.75rem, 2vw, 0.95rem)'}}>
                  Luggage not picked up?
                </p>
              </div>
            </div>
          </a>
        </div>
      </div>

      {/* Footer mit Impressum (Pflichtangabe, leicht auffindbar) */}
      <footer className="text-center pb-3">
        <a href="/impressum" className="text-white-50 text-decoration-none" style={{fontSize: 'clamp(0.8rem, 2vw, 0.95rem)'}}>
          Impressum
        </a>
      </footer>

      {/* Login Modal Overlay */}
      {showLogin && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{background: 'rgba(0,0,0,0.6)', zIndex: 9999, backdropFilter: 'blur(4px)'}}
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogin(false); }}
        >
          <div className="card shadow-lg border-0 mx-3" style={{maxWidth: '420px', width: '100%', borderRadius: '16px'}}>
            <div className="card-body p-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <i className="bi bi-cup-hot-fill text-warning me-2" style={{fontSize: '1.5rem'}}></i>
                  <span className="fw-bold" style={{fontSize: '1.2rem'}}>Anmelden</span>
                </div>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowLogin(false)}
                  aria-label="Schließen"
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="lastname" className="form-label">
                    <i className="bi bi-person-fill me-2"></i>Nachname
                  </label>
                  <input
                    id="lastname"
                    type="text"
                    className="form-control form-control-lg"
                    placeholder="Ihr Nachname"
                    value={lastname}
                    onChange={e => setLastname(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
                
                <div className="mb-3">
                  <label htmlFor="birthdate" className="form-label">
                    <i className="bi bi-calendar-fill me-2"></i>Geburtsdatum (für Mitarbeiter)
                  </label>
                  <input
                    id="birthdate"
                    type="text"
                    inputMode="numeric"
                    className="form-control form-control-lg"
                    value={birthdate}
                    onChange={handleBirthdateChange}
                    placeholder="TT.MM.JJJJ"
                    maxLength="10"
                  />
                  <small className="form-text text-muted">
                    Format: TT.MM.JJJJ (wird automatisch formatiert)
                  </small>
                </div>

                {!showAdminLogin ? (
                  <div className="mb-4 text-center">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-muted p-0"
                      onClick={() => setShowAdminLogin(true)}
                    >
                      <i className="bi bi-lock-fill me-1"></i>Als Admin anmelden
                    </button>
                  </div>
                ) : (
                  <div className="mb-4">
                    <label htmlFor="password" className="form-label">
                      <i className="bi bi-lock-fill me-2"></i>Passwort (für Admins)
                    </label>
                    <input
                      id="password"
                      type="password"
                      className="form-control form-control-lg"
                      placeholder="Ihr Passwort"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      autoFocus
                    />
                  </div>
                )}
                
                <button 
                  type="submit" 
                  className="btn btn-primary btn-lg w-100 mb-3"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Anmelden...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                      Anmelden
                    </>
                  )}
                </button>
                
                {error && (
                  <div className="alert alert-danger" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    {error}
                  </div>
                )}
                
                <div className="text-center mt-2">
                  <small className="text-muted">
                    Anmeldung mit Nachname + Passwort oder Nachname + Geburtsdatum
                  </small>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
